/**
 * v3 brief data model — ported from the wireframe (wireframe-data-v3.js +
 * wireframe-briefflow-v3.js zone vocabulary). Local UI state for now; later
 * chunks map this onto the real ZoneBrief / UnitMixBrief / PodiumBrief types
 * and the real World the generator consumes.
 */

/** One unit-mix category: enabled, an exact %, and lockable during rebalance. */
export interface MixEntry {
  pct: number;
  on: boolean;
  lock: boolean;
}

export interface Podium {
  on: boolean;
  floors: number;
  height: number;
}

export interface BoardBrief {
  floors: number;
  floorHeight: number;
  separation: number;
  /** building-height RANGE */
  minHeight: number;
  maxHeight: number;
  /** commercial podium — the only podium the engine supports */
  podium: Podium;
  /** one entry per UNIT_MIX category */
  mix: MixEntry[];
}

export interface UnitCategory {
  code: string;
  label: string;
  color: string;
}

/** The five occupancy categories the engine supports (no Studio). */
export const UNIT_MIX: UnitCategory[] = [
  { code: "1B2P", label: "1 bed (2p)", color: "#7c5cff" },
  { code: "2B3P", label: "2 bed (3p)", color: "#2a9d8f" },
  { code: "2B4P", label: "2 bed (4p)", color: "#e0a32e" },
  { code: "3B5P", label: "3 bed (5p)", color: "#e0683b" },
  { code: "3B6P", label: "3 bed (6p)", color: "#4a7ec7" },
];

export const UNIT_MIX_LABELS = UNIT_MIX.map((u) => u.label);

export const DEFAULT_BRIEF: BoardBrief = {
  floors: 6,
  floorHeight: 3.2,
  separation: 12,
  minHeight: 18,
  maxHeight: 30,
  podium: { on: false, floors: 2, height: 4.5 },
  mix: [
    { pct: 15, on: true, lock: false },
    { pct: 25, on: true, lock: false },
    { pct: 30, on: true, lock: false },
    { pct: 18, on: true, lock: false },
    { pct: 12, on: true, lock: false },
  ],
};

/** Sum of enabled categories' percentages. */
export const mixTotal = (mix: MixEntry[]): number =>
  mix.reduce((s, m) => s + (m.on ? m.pct : 0), 0);

/**
 * Rebalance the mix so enabled categories total 100. Locked categories and the
 * one at `keepIdx` (the one the user just edited) keep their %, the rest absorb
 * the remainder proportionally. Disabled categories are pinned to 0.
 */
export function rebalanceMix(mix: MixEntry[], keepIdx: number | null): MixEntry[] {
  const out = mix.map((m) => ({ ...m, pct: m.on ? m.pct : 0 }));
  const fixed = out.reduce((s, m, i) => s + (m.on && (m.lock || i === keepIdx) ? m.pct : 0), 0);
  const adj = out.map((m, i) => (m.on && !m.lock && i !== keepIdx ? i : -1)).filter((i) => i >= 0);
  const remaining = Math.max(0, 100 - fixed);
  const curSum = adj.reduce((s, i) => s + out[i].pct, 0);
  let assigned = 0;
  adj.forEach((idx, k) => {
    const share =
      k === adj.length - 1
        ? remaining - assigned
        : curSum > 0
          ? Math.round((remaining * out[idx].pct) / curSum)
          : Math.round(remaining / adj.length);
    out[idx].pct = Math.max(0, share);
    assigned += out[idx].pct;
  });
  return out;
}

/* ---- site zones (manually drawn) ---- */

export type ZoneTypeId = "Buildable" | "Exclusion" | "Parking" | "Access" | "Green zone";

export interface ZoneTypeDef {
  id: ZoneTypeId;
  abbr: string;
  note: string;
}

/** Exact zone-type vocabulary Tessa uses. Only Buildable gets the accent. */
export const ZONE_TYPES: ZoneTypeDef[] = [
  { id: "Buildable", abbr: "B", note: "designs generate here" },
  { id: "Exclusion", abbr: "X", note: "keep clear" },
  { id: "Parking", abbr: "P", note: "vehicle areas" },
  { id: "Access", abbr: "A", note: "routes & entry" },
  { id: "Green zone", abbr: "G", note: "landscape" },
];

export interface ZonePoint {
  x: number;
  y: number;
}

export interface BriefZone {
  id: string;
  type: ZoneTypeId;
  name: string;
  pts: ZonePoint[];
  /** fake hectares (placeholder until real geometry) */
  area: number;
}

export interface ZoneStyle {
  stroke: string;
  fill: string;
  dash: string;
  accent?: boolean;
}

export function zoneStyle(type: ZoneTypeId): ZoneStyle {
  switch (type) {
    case "Buildable":
      return { stroke: "#7c5cff", fill: "rgba(124,92,255,0.10)", dash: "4 2", accent: true };
    case "Exclusion":
      return { stroke: "#6b7280", fill: "rgba(107,114,128,0.06)", dash: "2 3" };
    case "Parking":
      return { stroke: "#6b7280", fill: "rgba(107,114,128,0.08)", dash: "0" };
    case "Access":
      return { stroke: "#9ca3af", fill: "rgba(156,163,175,0.05)", dash: "6 2 1 2" };
    case "Green zone":
      return { stroke: "#6b7280", fill: "rgba(107,114,128,0.05)", dash: "1 3" };
    default:
      return { stroke: "#9ca3af", fill: "none", dash: "3 3" };
  }
}

const polyArea = (p: ZonePoint[]): number => {
  let a = 0;
  for (let i = 0; i < p.length; i++) {
    const j = (i + 1) % p.length;
    a += p[i].x * p[j].y - p[j].x * p[i].y;
  }
  return Math.abs(a / 2);
};

export const centroid = (p: ZonePoint[]): ZonePoint => ({
  x: p.reduce((s, q) => s + q.x, 0) / p.length,
  y: p.reduce((s, q) => s + q.y, 0) / p.length,
});

/** percent² → fake hectares (placeholder). */
export const haOf = (pts: ZonePoint[]): number => +(polyArea(pts) * 0.00025).toFixed(2);

/* ---- site overlays (BRW / land-value stencils) ---- */

export type OverlaySource = "Upload" | "URL" | "Catalog";

export interface Overlay {
  name: string;
  source: OverlaySource;
}

/** German land-value / planning catalog (matches the real wmsCatalog cities). */
export const OVERLAY_CATALOG = [
  "Hamburg · Bodenrichtwert",
  "Berlin · BRW 2024",
  "München · B-Plan zones",
  "Köln · BRW 2023",
];

/* ---- a brief section (one brief on the board) ---- */

export interface BriefSectionState {
  id: string;
  label: string;
  date: string;
  brief: BoardBrief;
  /** JSON snapshot of the brief that produced this revision (immutability baseline) */
  appliedBrief: string;
  /** map is centered on Hamburg by default; fills once the user searches */
  address: string;
  zones: BriefZone[];
  overlays: Overlay[];
}

export function cloneBrief(b: BoardBrief): BoardBrief {
  return { ...b, podium: { ...b.podium }, mix: b.mix.map((m) => ({ ...m })) };
}

export function makeBriefSection(n: number): BriefSectionState {
  const brief = cloneBrief(DEFAULT_BRIEF);
  return {
    id: "b" + Math.random().toString(36).slice(2, 7),
    label: "Revision " + n,
    date: "Jun 10, 2026",
    brief,
    appliedBrief: JSON.stringify(brief),
    address: "",
    zones: [],
    overlays: [],
  };
}
